import type { ReactNode } from 'react';
import { Avatar } from './Avatar.js';
import { cn } from './cn.js';
import { linkTextClasses } from './link.js';
import { NotProvided } from './NotProvided.js';

/**
 * Um grupo de registros ligados ao que está aberto — "Associado", "Terceiros",
 * "Itens acionados" —, com a contagem ao lado do título.
 *
 * É a coluna da esquerda da `DeskModal`: **identifica, não detalha.** O
 * detalhe é a ficha de cada um, e o nome leva até ela. Por isso não há caixa
 * por linha — uma lista leve, separada por traço fino, para muitas linhas
 * caberem sem rolar.
 */
export function RelatedGroup({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  /** Aparece ao lado do título quando é maior que zero. */
  count?: number;
  /** A frase de quando o grupo não tem ninguém — "Nenhum terceiro neste sinistro." */
  empty?: ReactNode;
  /** As `RelatedItem`. Sem filhos, aparece o `empty`. */
  children?: ReactNode;
}) {
  const hasItems = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <section aria-label={title} className="flex flex-col gap-1 [&+&]:mt-2">
      <h3 className="flex items-center gap-2 px-2 text-xs font-semibold tracking-wide text-texto-tenue uppercase">
        {title}
        {count !== undefined && count > 0 && (
          <span className="rounded-pilula bg-superficie-suave px-1.5 text-xs tracking-normal text-texto-suave tabular-nums normal-case">
            {count}
          </span>
        )}
      </h3>
      {hasItems ? (
        <ul className="flex flex-col divide-y divide-borda-suave">{children}</ul>
      ) : (
        empty && <p className="px-2 text-sm text-texto-tenue">{empty}</p>
      )}
    </section>
  );
}

/**
 * Uma linha do `RelatedGroup`: quem é (avatar ou mídia), o nome que abre o
 * registro, uma linha de detalhe e, embaixo, o que mais o módulo quiser — na
 * mesa do sinistro, a `StepPath` dos departamentos por onde o aviso passou.
 */
export function RelatedItem({
  name,
  avatarTone = 'escura',
  visual,
  onOpen,
  openLabel,
  subtitle,
  selected = false,
  children,
}: {
  /** O nome; `null` vira `NotProvided`, e o avatar fica vazio. */
  name: string | null;
  avatarTone?: 'escura' | 'marca';
  /** No lugar do avatar — a foto de um item acionado. */
  visual?: ReactNode;
  /** Abrir o registro. Sem ela, o nome é texto. */
  onOpen?: () => void;
  /** "Abrir a ficha do aviso #214" — a dica e o rótulo do nome. */
  openLabel?: string;
  /** A linha de baixo do nome: "Aviso #214 · ABC1D23". */
  subtitle?: ReactNode;
  /** O registro que está aberto na mesa agora: fundo suave. */
  selected?: boolean;
  children?: ReactNode;
}) {
  const title = name ?? <NotProvided />;
  return (
    <li
      className={cn('flex gap-2.5 rounded-borda p-2', selected && 'bg-superficie-suave')}
    >
      {visual ?? <Avatar nome={name} size="sm" tone={avatarTone} />}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {onOpen ? (
          <button
            type="button"
            onClick={onOpen}
            title={openLabel}
            className={linkTextClasses(
              'justify-start text-left text-sm font-semibold break-words',
            )}
          >
            {title}
          </button>
        ) : (
          <span className="text-sm font-semibold break-words">{title}</span>
        )}
        {subtitle && <span className="text-xs text-texto-suave">{subtitle}</span>}
        {children && <div className="mt-1">{children}</div>}
      </div>
    </li>
  );
}
